// src/ui/steps/step4-festival-zzp.jsx: Festival ZZP-flow eindscherm
// Toont openingsbod = werkdagen × dagtarief (= uurtarief × 7,6). Festivaldagen zijn
// informatief (onderhandelpunt SAV, niet in het bedrag).
import React from "react";
import { FW, FwButton, FwCard, StepHeader, ResultaatSamenvatting, DeelResultaat } from "../primitives";
import { calc } from "../../lib/calc";
import { config } from "../../lib/config";
import { Aanname, OpslagTabel, RolOmschrijvingDetail } from "./step4-result";
import { exportFestivalZzp } from "../../lib/docx-export";

export const StepFestivalZzpResult = ({ state, setState, back, restart }) => {
  const huidigeRol = state.rollen?.[0];
  const result = huidigeRol ? calc.berekenFestivalZzp(huidigeRol) : null;

  if (!huidigeRol || !result) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk: ga terug en vul het aantal werkdagen in.</div>;
  }

  const btwPct = Number(config.generiek?.btw_pct) || 0.21;
  const totaalExBtw = result.openingsbod;
  const btw = totaalExBtw * btwPct;
  const totaalInclBtw = totaalExBtw + btw;

  const emailText = generateEmailFestivalZzp(state, huidigeRol, result, totaalInclBtw);
  const downloadWord = () => exportFestivalZzp({ state, huidigeRol, result, totaalExBtw, btw, totaalInclBtw });

  return (
    <div className="result-page">
      <StepHeader
        tag="Filmfestival"
        vorm={state.vorm}
        title="Jouw openingsbod voor onderhandeling"
        sub="Op basis van de Richtlijn zzp-starttarieven en salarissen Filmfestivals. Dit is je startpunt: jouw ervaring, reputatie, specialisme en de complexiteit van het werk kunnen van invloed zijn op de hoogte van je tarief. Houd hier rekening mee bij het bepalen van je openingsbod voor de onderhandeling."
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={true}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={`Afdeling ${huidigeRol.dep}`}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          {/* Het bedrag staat al in de pay-off onderaan, dus we starten hier
              direct met de onderbouwing (net als bij de loondienst-flow). */}
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Onderbouwing van jouw openingsbod</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op de aparte elementen om te kijken welke onderbouwing je hebt ingevuld of om deze aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als omschrijving van de opdracht. De digitale assistent gebruikt deze tekst om een rol en schaal te suggereren."
                onEdit={() => { back(); back(); back(); }}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Type opdracht"
              waarde="Filmfestival: zzp"
              uitleg="Voor filmfestivals geldt een eigen Fair Pay richtlijn met 12 schalen (1-12, Cao Toneel & Dans). Voor zzp'ers wordt het zzp-starttarief gebruikt, met een opslagfactor van 69,26%."
              onEdit={() => restart()}
            />
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg="Op basis van jouw omschrijving is deze rol voorgesteld uit het Functiehandboek Referentiefuncties Filmfestivals, of je hebt deze zelf geselecteerd."
              onEdit={() => { back(); back(); back(); }}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={true} />}
            />
            <Aanname
              label="Schaal"
              waarde={`Schaal ${huidigeRol.niveau} (${huidigeRol.rol})`}
              uitleg={`Schaal ${huidigeRol.niveau} (CAO Toneel & Dans) geeft het minimum zzp-uurtarief van ${calc.fmt(result.uurtarief)}/u, oftewel ${calc.fmt(result.dagtarief)} per dag (× 7,6 u).`}
              onEdit={() => { back(); back(); }}
            />
            <Aanname
              label="Opslagfactor"
              waarde="69,26% (digiPACCT)"
              uitleg="Voor filmfestivals geldt de digiPACCT-methode: bedrijfskosten (15%), niet-declarabele uren (17%), vakantietoeslag (8%), ziekte/arbeidsongeschiktheid (14,5%), ZVW (5,32%) en pensioen (9,44%). Samen: 69,26%."
              detail={<OpslagTabel isFestival={true} />}
            />
            <Aanname
              label="Werkdagen"
              waarde={`${result.werkdagenTotaal} dagen`}
              uitleg="Het aantal werkdagen dat je aan deze opdracht besteedt. Eén werkdag = 7,6 uur (excl. pauze, conform SAV)."
              onEdit={() => back()}
              detail={
                <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.6, marginBottom: 12, display: "grid", gap: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Voorbereiding festival</span><span style={{ fontWeight: 700, color: FW.navy }}>{Number(huidigeRol.werkdagen?.voorbereiding) || 0} dagen</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Festivaldagen</span><span style={{ fontWeight: 700, color: FW.navy }}>{Number(huidigeRol.werkdagen?.festivaldagen) || 0} dagen</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Afronding festival</span><span style={{ fontWeight: 700, color: FW.navy }}>{Number(huidigeRol.werkdagen?.afronding) || 0} dagen</span></div>
                </div>
              }
            />
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>Goed om te weten</div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>De <b>complexiteit</b> van de opdracht, jouw <b>ervaring</b>, <b>reputatie</b> of <b>specialisme</b> kun je meenemen in je onderhandeling.</li>
              <li>Over eventuele <b>reiskosten</b>, <b>apparatuur</b>, <b>materialen</b>, <b>inhuur derden</b> kun je separaat afspraken maken en apart factureren.</li>
              <li>Aanvullende afspraken staan in <b>Standaard Basisregeling Secundaire Arbeidsvoorwaarden (SAV) Filmfestivals</b>, punt 30 voor zzp'ers.</li>
              <li>Het tarief bevat al een <b>opslagfactor van 69,26%</b> voor niet-declarabele tijd, niet-declarabele kosten, vakantie, ziekte en pensioen.</li>
            </ul>
          </FwCard>
        </div>
      </div>

      <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "40px 44px", marginTop: 32 }}>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Dagtarief</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmt(result.dagtarief)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Uurtarief {calc.fmt(result.uurtarief)} × 7,6 u · incl. 69,26% opslag
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Werkdagen</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {result.werkdagenTotaal}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              {result.festivaldagen > 0 ? `Waarvan ${result.festivaldagen} festivaldag${result.festivaldagen === 1 ? "" : "en"}` : "Geen festivaldagen apart opgegeven"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Openingsbod</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(result.openingsbod)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Excl. btw · incl: {calc.fmtInt(totaalInclBtw)}
            </div>
          </div>
        </div>
      </FwCard>

      <DeelResultaat state={state} emailText={emailText} onDownload={downloadWord} />

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Pas werkdagen aan</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

function generateEmailFestivalZzp(state, rol, result, totaalInclBtw) {
  const fdRegel = result.festivaldagen > 0 ? `, waarvan ${result.festivaldagen} festivaldag${result.festivaldagen === 1 ? "" : "en"}` : "";
  return `Beste [opdrachtgever],

Hierbij een openingsbod voor mijn inzet als ${rol.rol} (${rol.dep}) op jouw filmfestival.

Op basis van de Fair Pay richtlijnen 2026 voor filmfestivals (schaal ${rol.niveau} volgens CAO Toneel & Dans) stel ik graag het volgende voor:

• Uurtarief: ${calc.fmt(result.uurtarief)} (incl. 69,26% opslag)
• Dagtarief: ${calc.fmt(result.dagtarief)} (uurtarief × 7,6 u, conform SAV)
• Werkdagen: ${result.werkdagenTotaal}${fdRegel}
• Openingsbod: ${calc.fmtInt(result.openingsbod)} excl. btw (${calc.fmtInt(totaalInclBtw)} incl. btw)

Conform de Standaard Basisregeling SAV Filmfestivals ga ik graag in gesprek over festivaldag-toeslag, nachtwerk-toeslag, vergoeding van reisuren als arbeidstijd, en lunch/diner tijdens het festival.

Met vriendelijke groet,
[jouw naam]`;
}
